/**
 * @fileoverview Badge component for linked cost with popover to remove assignment.
 * @module features/payments/components/CostBadge
 */

import { useState, type ReactElement } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Badge } from '@/shared/components/ui'
import { Button } from '@/components/ui/button'
import { unlinkPaymentFromCost } from '@/features/indirect-costs/api/indirectCostsLinksService'

export interface CostBadgeProps {
  /** Payment UUID */
  paymentId: string
  /** Indirect cost UUID for unlinking */
  costUuid: string
  /** Subcategory display name */
  displayName: string
}

/**
 * Badge displaying a linked cost subcategory with popover to remove the assignment.
 */
export default function CostBadge({
  paymentId,
  costUuid,
  displayName,
}: CostBadgeProps): ReactElement {
  const { t } = useTranslation('features-indirect-costs')
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)

  const unlinkMutation = useMutation({
    mutationFn: async () => {
      await unlinkPaymentFromCost(costUuid, paymentId)
    },
    onSuccess: () => {
      toast.success(t('linkedPayments.unlink.success'))
      void queryClient.invalidateQueries({ queryKey: ['payments'] })
      void queryClient.invalidateQueries({ queryKey: ['payments', 'linked-costs', paymentId] })
      void queryClient.invalidateQueries({ queryKey: ['indirect-costs'] })
      setOpen(false)
    },
    onError: (err: unknown) => {
      const message =
        err instanceof Error ? err.message : t('linkedPayments.unlink.error')
      toast.error(message)
    },
  })

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md"
          onPointerDown={e => e.stopPropagation()}
          onClick={e => e.stopPropagation()}
        >
          <Badge variant="secondary" className="text-[11px] cursor-pointer transition-colors hover:bg-muted-foreground/20">
            {displayName}
          </Badge>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3" align="start" sideOffset={4}>
        <div className="flex flex-col gap-2">
          <p className="text-sm font-medium">{displayName}</p>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            onClick={() => unlinkMutation.mutate()}
            disabled={unlinkMutation.isPending}
          >
            {unlinkMutation.isPending ? t('linkedPayments.unlink.pending') : t('linkedPayments.unlink.action')}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
